import React from "react";
import { Stethoscope, FlaskConical, ShieldPlus, MessageCircle, Info } from "lucide-react";
import { waUrl } from "@/lib/whatsapp";
import { useApp } from "@/context/AppContext";
import { contactGate } from "@/lib/contactGate";

export const TEMPLATE_INFO = {
  hospital: { label: "Hospital", doctors: "Departments & Doctors", tests: "Tests & Scans", facilities: "Facilities & Emergency" },
  pathology_lab: { label: "Pathology Lab", doctors: "Pathologists & Staff", tests: "Tests & Rates", facilities: "Facilities & Home Collection" },
  radiology_centre: { label: "Radiology Centre", doctors: "Radiologists & Departments", tests: "Scans & Rates", facilities: "Facilities & Emergency" },
};
export const FACILITY_LABELS = { ambulance: "Ambulance", icu: "ICU", home_sample_collection: "Home sample collection", emergency_24x7: "24x7 Emergency" };

const Section = ({ icon: Icon, title, testid, children }) => (
  <section className="bg-white border border-slate-100 rounded-2xl p-4 sm:p-5 min-w-0" data-testid={testid}>
    <h2 className="font-display text-lg font-bold flex items-center gap-2 mb-3 break-words"><Icon className="w-5 h-5 text-emerald-700 flex-shrink-0" /> {title}</h2>
    {children}
  </section>
);
const Empty = ({ testid }) => <div className="text-sm text-slate-500" data-testid={testid}>Not added by the business yet.</div>;

function AppointmentButton({ name, number, locked }) {
  const { user } = useApp();
  if (locked) return (
    <button type="button" onClick={() => contactGate(user, locked)} className="inline-flex items-center justify-center gap-2 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-5 py-2.5 text-sm font-semibold transition-colors w-full sm:w-auto" data-testid="bp-whatsapp-appointment-btn">
      <MessageCircle className="w-4 h-4" /> Request appointment on WhatsApp
    </button>
  );
  if (!number) return <div className="text-sm text-slate-500" data-testid="bp-whatsapp-missing">WhatsApp number not added by the business yet.</div>;
  const digits = number.length === 10 ? `91${number}` : number;
  const msg = `Namaste ${name}, mujhe appointment ke baare mein jaankari chahiye. (VerB se)`;
  return (
    <a href={waUrl(digits, msg)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-5 py-2.5 text-sm font-semibold transition-colors w-full sm:w-auto" data-testid="bp-whatsapp-appointment-btn">
      <MessageCircle className="w-4 h-4" /> Request appointment on WhatsApp
    </a>
  );
}

// Public, read-only rendering of business-entered content. Nothing here is stored per visitor.
export default function BusinessProfileSections({ name, profile }) {
  const p = profile || {};
  const t = TEMPLATE_INFO[p.template] || TEMPLATE_INFO.hospital;
  const facilities = Object.entries(p.facilities || {}).filter(([, f]) => f && f.available !== null && f.available !== undefined);
  return (
    <div className="mt-8 space-y-4" data-testid="business-profile-sections">
      <div className="text-xs font-semibold uppercase tracking-wider text-emerald-700" data-testid="bp-template-label">{t.label}</div>
      <Section icon={Stethoscope} title={t.doctors} testid="bp-section-doctors">
        {p.doctors?.length ? (
          <div className="grid sm:grid-cols-2 gap-2">
            {p.doctors.map((d, i) => (
              <div key={i} className="rounded-xl bg-slate-50 p-3 min-w-0" data-testid={`bp-doctor-${i}`}>
                <div className="font-semibold text-slate-900 break-words">{d.name}</div>
                {(d.department || d.specialty) && <div className="text-xs text-slate-600 break-words">{[d.department, d.specialty].filter(Boolean).join(" · ")}</div>}
                {d.opd_timings && <div className="text-xs text-slate-500 mt-1 break-words">OPD: {d.opd_timings}</div>}
              </div>
            ))}
          </div>
        ) : <Empty testid="bp-doctors-empty" />}
      </Section>
      <Section icon={FlaskConical} title={t.tests} testid="bp-section-tests">
        {p.tests?.length ? (
          <div className="divide-y divide-slate-100">
            {p.tests.map((x, i) => (
              <div key={i} className="flex items-start justify-between gap-3 py-2" data-testid={`bp-test-${i}`}>
                <div className="min-w-0"><div className="text-sm font-semibold text-slate-900 break-words">{x.name}</div>{x.category && <div className="text-xs text-slate-500 break-words">{x.category}</div>}</div>
                <div className="text-sm font-bold text-emerald-800 whitespace-nowrap" data-testid={`bp-test-price-${i}`}>{x.price == null ? "Contact for price" : `₹${x.price}`}</div>
              </div>
            ))}
          </div>
        ) : <Empty testid="bp-tests-empty" />}
      </Section>
      <Section icon={ShieldPlus} title={t.facilities} testid="bp-section-facilities">
        {facilities.length ? (
          <div className="grid sm:grid-cols-2 gap-2">
            {facilities.map(([k, f]) => (
              <div key={k} className="rounded-xl bg-slate-50 p-3 min-w-0" data-testid={`bp-facility-${k}`}>
                <div className="text-sm font-semibold text-slate-900">{FACILITY_LABELS[k] || k}: <span className={f.available ? "text-emerald-700" : "text-slate-500"}>{f.available ? "Available" : "Not available"}</span></div>
                {f.details && <div className="text-xs text-slate-600 break-words mt-0.5">{f.details}</div>}
              </div>
            ))}
          </div>
        ) : <Empty testid="bp-facilities-empty" />}
        <div className="mt-3 text-[11px] text-slate-500 flex items-start gap-1.5"><Info className="w-3.5 h-3.5 flex-shrink-0 mt-px" /> Information provided by the business. Please confirm by calling before visiting.</div>
      </Section>
      <Section icon={MessageCircle} title="Appointment request" testid="bp-section-appointment">
        <p className="text-xs text-slate-500 mb-3">Opens WhatsApp with a ready message. You send it directly to the business; VerB does not save appointment or health details.</p>
        <AppointmentButton name={name} number={p.appointment_whatsapp} locked={p.contact_locked} />
      </Section>
    </div>
  );
}
